import { useEffect, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Button,
  Center,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Spacer,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';

import {
  AddCircleOutline,
  DocumentUploadOutline,
  CustomAlert,
  DropdownOption,
  FileUploadType,
  FilterData,
  Filters,
  IndividualStatus,
  MultiSelectionData,
  SearchNormalOutline,
} from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import EmployeesStatus from 'app/components/Employees/EmployeesStatus';
import CreateEmployeeModal from 'app/components/Employees/CreateEmployee/CreateEmployeeModal';
import { AddPersonModal } from 'app/components/Employees/AddPerson';
import DeleteIndividualModal from 'app/components/Individuals/DeleteIndividual/DeleteIndividualModal';
import InviteIndividualModal from 'app/components/Individuals/InviteIndividual/InviteIndividualModal';
import ManageUnpaidLeaveModal from 'app/components/Employees/ManageUnpaidLeaveModal';
import PauseEmployeeModal from 'app/components/Employees/PauseEmployee/PauseEmployeeModal';
import TerminateEmployeeModal from 'app/components/Employees/TerminateEmployee/TerminateEmployeeModal';
import EditEmployeeTerminationDateModal from 'app/components/Employees/EditEmployeeTerminationDate/EditEmployeeTerminationDateModal';
import ReactivateEmployeeModal from 'app/components/Employees/ReactivateEmployee/ReactivateEmployeeModal';
import RewardPointsModal from 'app/components/Employees/RewardPointsModal';

import { defaultPageSize } from 'app/constants/table';
import { Status } from 'app/constants/individual';

import useFetchFileUploadsPage from 'app/hooks/useFetchFileUploadsPage';
import useFetchEmployeesPage from 'app/hooks/useFetchEmployeesPage';
import useFetchPayCalendars from 'app/hooks/useFetchPayCalendars';
import useDebounce from 'app/hooks/useDebounce';

import { useManagingCompanyStore } from 'app/store';

import { getIndividualColumns } from 'app/utils/table/individuals';
import {
  getEmployeeProfilePath,
  getImportEmployeesPath,
  getPayCalendarsPath,
} from 'app/utils/routes';
import { Table, IndividualActions, IndividualBulkActions } from 'app/components/UI/Table';

import type { Filter } from '@aslanpay/portal-common-lib';

type LeaveDateFilter = 'WITH_LEAVE_DATE' | 'WITHOUT_LEAVE_DATE';

const employeeStatusOptions: DropdownOption[] = Object.entries(Status)
  .filter(([value]) => !['DELETED', 'LEAVING'].includes(value))
  .map(([value, data]) => ({
    value,
    text: data.value,
  }));

const leaveDateFilterOptions: DropdownOption[] = [
  { value: 'WITH_LEAVE_DATE', text: 'Has leave date' },
  { value: 'WITHOUT_LEAVE_DATE', text: 'No leave date' },
];

function Employees() {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { company } = useManagingCompanyStore.getState();

  const {
    isOpen: isCreateEmployeeOpen,
    onOpen: onOpenCreateEmployee,
    onClose: onCloseCreateEmployee,
  } = useDisclosure();

  const {
    isOpen: isInviteEmployeeOpen,
    onOpen: onOpenInviteEmployee,
    onClose: onCloseInviteEmployee,
  } = useDisclosure();

  const {
    isOpen: isPauseEmployeeOpen,
    onOpen: onOpenPauseEmployee,
    onClose: onClosePauseEmployee,
  } = useDisclosure();

  const {
    isOpen: isReactivateEmployeeOpen,
    onOpen: onOpenReactivateEmployee,
    onClose: onCloseReactivateEmployee,
  } = useDisclosure();

  const {
    isOpen: isManageUnpaidLeaveOpen,
    onOpen: onOpenManageUnpaidLeave,
    onClose: onCloseManageUnpaidLeave,
  } = useDisclosure();

  const {
    isOpen: isTerminateEmployeeOpen,
    onOpen: onOpenTerminateEmployee,
    onClose: onCloseTerminateEmployee,
  } = useDisclosure();

  const {
    isOpen: isEditEmployeeTerminationDateOpen,
    onOpen: onOpenEditEmployeeTerminationDate,
    onClose: onCloseEditEmployeeTerminationDate,
  } = useDisclosure();

  const {
    isOpen: isRewardPointsOpen,
    onOpen: onOpenRewardPoints,
    onClose: onCloseRewardPoints,
  } = useDisclosure();

  const {
    isOpen: isDeleteEmployeeOpen,
    onOpen: onOpenDeleteEmployee,
    onClose: onCloseDeleteEmployee,
  } = useDisclosure();

  const {
    isOpen: isNeedPayCalendarOpen,
    onOpen: onOpenNeedPayCalendar,
    onClose: onCloseNeedPayCalendar,
  } = useDisclosure();

  const [employeesActionIds, setEmployeesActionIds] = useState<string[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('query') || '');
  const [statusFilter, setStatusFilter] = useState<IndividualStatus[]>(
    searchParams.get('status') ? (searchParams.get('status')?.split(',') as IndividualStatus[]) : []
  );
  const [leaveDateFilter, setLeaveDateFilter] = useState<LeaveDateFilter | undefined>(() => {
    const terminationDateParam = searchParams.get('isTerminationDateSet');

    if (terminationDateParam === 'true') {
      return 'WITH_LEAVE_DATE';
    }

    if (terminationDateParam === 'false') {
      return 'WITHOUT_LEAVE_DATE';
    }

    return undefined;
  });

  const currentPage = searchParams.get('page') ? Number(searchParams.get('page')) - 1 : 0;

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: currentPage,
    pageSize: defaultPageSize,
  });
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  const filters: Filter[] = [
    {
      name: 'Employee status',
      type: 'category',
      data: employeeStatusOptions,
      selectedValues: statusFilter,
    },
    {
      name: 'Leave date',
      type: 'category',
      data: leaveDateFilterOptions,
      selectedValues: leaveDateFilter ? [leaveDateFilter] : [],
      singleSelection: true,
    },
  ];

  const resetPagination = () => {
    setPagination({ pageIndex: 0, pageSize: defaultPageSize });
  };

  const getIsTerminationDateSet = () => {
    if (leaveDateFilter === 'WITH_LEAVE_DATE') {
      return true;
    }

    if (leaveDateFilter === 'WITHOUT_LEAVE_DATE') {
      return false;
    }

    return undefined;
  };

  const isTerminationDateSet = getIsTerminationDateSet();

  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    resetPagination();
  };

  const onSearchQueryChangedDebounced = useDebounce(handleSearchQueryChange);

  const [sorting, setSorting] = useState<SortingState>(() => [
    ...(searchParams.get('sortBy')
      ? [
          {
            id: searchParams.get('sortBy') as string,
            desc: searchParams.get('sort') === 'DESC',
          },
        ]
      : []),
  ]);

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data: employees, isLoading: isLoadingEmployees } = useFetchEmployeesPage({
    companyId: [company?.id || ''],
    status: statusFilter,
    isTerminationDateSet,
    query: searchQuery,
    sortBy: sorting[0]?.id,
    sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    page: pageIndex,
    size: pageSize,
  });

  // EDF upload types that count as an in-progress "bulk upload" for this company.
  const inProgressEdfUploadType = (
    company?.type === 'GIFTING_ONLY' ? 'GIFTING_ONLY_EMPLOYEE_DATA_FILE' : 'EMPLOYEE_DATA_FILE'
  ) as FileUploadType;

  const IN_PROGRESS_FILE_UPLOAD_STATUSES = [
    'UPLOAD_REQUESTED',
    'ANALYSIS_IN_PROGRESS',
    'AWAITING_CLIENT_CONFIRMATION',
    'CLIENT_CONFIRMED',
    'VALIDATION_IN_PROGRESS',
    'VALIDATED',
    'PROCESSING_IN_PROGRESS',
  ];

  const { data: fileUploadsPage, isLoading: isLoadingImportingBulkImports } =
    useFetchFileUploadsPage(
      {
        companyId: company?.id || '',
        type: inProgressEdfUploadType,
        page: 0,
        size: 25,
      },
      !!company?.id
    );

  const importingBulkImportsTotal = (fileUploadsPage?.list ?? []).filter((file) =>
    IN_PROGRESS_FILE_UPLOAD_STATUSES.includes(file.status)
  ).length;

  const { data: payCalendars } = useFetchPayCalendars({
    companyId: [company?.id || ''],
    page: 0,
    size: 1,
  });

  const handleEditProfile = (ids: number[]) => {
    const employeeId = employees?.list[ids[0]]?.id || '';

    navigate(getEmployeeProfilePath(employeeId));
  };

  const handleOpenPauseEmployeeModal = (ids: number[]) => {
    const employeeIds = ids.map((id) => employees?.list[id]?.id || '');

    setEmployeesActionIds(employeeIds);
    onOpenPauseEmployee();
  };

  const handleOpenReactivateEmployeeModal = (ids: number[]) => {
    const employeeIds = ids.map((id) => employees?.list[id]?.id || '');

    setEmployeesActionIds(employeeIds);
    onOpenReactivateEmployee();
  };

  const handleOpenTerminateEmployeeModal = (ids: number[]) => {
    const employeeIds = ids.map((id) => employees?.list[id]?.id || '');

    setEmployeesActionIds(employeeIds);
    onOpenTerminateEmployee();
  };

  const handleOpenEditTerminateEmployeeModal = (ids: number[]) => {
    const employeeIds = ids.map((id) => employees?.list[id]?.id || '');

    setEmployeesActionIds(employeeIds);
    onOpenEditEmployeeTerminationDate();
  };

  const handleOpenInviteEmployeeModal = (ids: number[]) => {
    const employeeIds = ids.map((id) => employees?.list[id]?.id || '');

    setEmployeesActionIds(employeeIds);
    onOpenInviteEmployee();
  };

  const handleOpenDeleteEmployeeModal = (ids: number[]) => {
    const employeeIds = ids.map((id) => employees?.list[id]?.id || '');

    setEmployeesActionIds(employeeIds);
    onOpenDeleteEmployee();
  };

  const handleOpenManageUnpaidLeaveModal = (ids: number[]) => {
    const employeeIds = ids.map((id) => employees?.list[id]?.id || '');

    setEmployeesActionIds(employeeIds);
    onOpenManageUnpaidLeave();
  };

  const handleOpenRewardPointsModal = (ids: number[]) => {
    const employeeIds = ids.map((id) => employees?.list[id]?.id || '');

    setEmployeesActionIds(employeeIds);
    onOpenRewardPoints();
  };

  const handleInvalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchEmployeesPage'] });
    queryClient.invalidateQueries({ queryKey: ['fetchEmployeesStats'] });

    setEmployeesActionIds([]);
    setRowSelection({});
  };

  const handleDeleteEmployees = () => {
    const totalPages = Math.ceil((employees?.total || 0) / pageSize);

    if (
      totalPages > 1 &&
      pageIndex === totalPages - 1 &&
      employeesActionIds.length === employees?.list.length
    ) {
      setPagination({ pageIndex: pageIndex - 1, pageSize: defaultPageSize });
    }

    handleInvalidateQueries();
  };

  const actions: IndividualActions = {
    edit: handleEditProfile,
    invite: handleOpenInviteEmployeeModal,
    pause: handleOpenPauseEmployeeModal,
    reactivate: handleOpenReactivateEmployeeModal,
    setUnpaid: handleOpenManageUnpaidLeaveModal,
    removeUnpaid: handleOpenManageUnpaidLeaveModal,
    terminate: handleOpenTerminateEmployeeModal,
    editTerminate: handleOpenEditTerminateEmployeeModal,
    rewardPoints: handleOpenRewardPointsModal,
    delete: handleOpenDeleteEmployeeModal,
  };

  const bulkActions: IndividualBulkActions = {
    invite: { action: handleOpenInviteEmployeeModal },
    delete: { action: handleOpenDeleteEmployeeModal },
  };

  const handleStatusChange = (values: (string | number)[]) => {
    setStatusFilter(values as IndividualStatus[]);
    resetPagination();
  };

  const handleLeaveDateChange = (value: string | number | undefined) => {
    if (!value) {
      setLeaveDateFilter(undefined);
      resetPagination();
      return;
    }

    setLeaveDateFilter(value as LeaveDateFilter);
    resetPagination();
  };

  const handleFilterClick = (filterName: string) => {
    setActiveFilter(filterName);
  };

  const handleFilterChange = (filterName: string, newValues: string[]) => {
    switch (filterName) {
      case 'Search': {
        const value = newValues[0] || '';

        setSearchQuery(value);
        resetPagination();
        break;
      }
      case 'Employee status':
        handleStatusChange(newValues);
        break;
      case 'Leave date':
        handleLeaveDateChange(newValues.at(-1));
        break;
      default:
        break;
    }
  };

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) => {
    return (
      (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
      String(filterValue)
    );
  };

  useEffect(() => {
    setSearchParams(
      {
        page: `${pageIndex + 1}`,
        ...(searchQuery && { query: searchQuery }),
        ...(sorting[0] && { sortBy: sorting[0]?.id, sort: sorting[0]?.desc ? 'DESC' : 'ASC' }),
        ...(statusFilter.length > 0 && { status: statusFilter.join(',') }),
        ...(leaveDateFilter && {
          isTerminationDateSet: leaveDateFilter === 'WITH_LEAVE_DATE' ? 'true' : 'false',
        }),
      },
      { replace: true }
    );
  }, [pageIndex, searchQuery, sorting, statusFilter, leaveDateFilter, setSearchParams]);

  const renderBulkUploadInProgressAlert = () => {
    if (importingBulkImportsTotal) {
      const message = company
        ? 'Bulk upload in progress...'
        : `${importingBulkImportsTotal} bulk upload${
            importingBulkImportsTotal > 1 ? 's' : ''
          } in progress...`;

      return (
        <Flex>
          <CustomAlert variant="simple" status="warning" title={message} boxShadow="none" />
        </Flex>
      );
    }

    return undefined;
  };

  const PAGE_SIZE_OPTIONS = [
    { value: 15, text: '15 rows / page' },
    { value: 25, text: '25 rows / page' },
    { value: 50, text: '50 rows / page' },
  ];

  return (
    <AuthenticatedPageWrapper title="Employees">
      <Flex gap="10px">
        <Text as="h2" textStyle="h2" color="brand.darkPurple">
          Employees
        </Text>
        <Spacer />
        <Button
          leftIcon={<AddCircleOutline boxSize="20px" />}
          variant="outline"
          size="sm"
          onClick={() => {
            if (company?.type !== 'GIFTING_ONLY' && !payCalendars?.total) {
              onOpenNeedPayCalendar();
            } else {
              onOpenCreateEmployee();
            }
          }}
        >
          {company?.type === 'GIFTING_ONLY' ? 'Add a person' : 'Create manually'}
        </Button>
        <Button
          leftIcon={<DocumentUploadOutline boxSize="20px" />}
          size="sm"
          onClick={() => {
            if (!payCalendars?.total) {
              onOpenNeedPayCalendar();
            } else {
              navigate(getImportEmployeesPath());
            }
          }}
        >
          Bulk import
        </Button>

        {company?.type === 'GIFTING_ONLY' ? (
          <AddPersonModal
            isOpen={isCreateEmployeeOpen}
            onClose={onCloseCreateEmployee}
            companyId={company.id}
            onSuccess={handleInvalidateQueries}
          />
        ) : (
          <CreateEmployeeModal
            companyId={company?.id}
            isOpen={isCreateEmployeeOpen}
            onClose={onCloseCreateEmployee}
          />
        )}
        <InviteIndividualModal
          selectedIndividuals={employeesActionIds}
          isOpen={isInviteEmployeeOpen}
          onClose={onCloseInviteEmployee}
          onInvite={handleInvalidateQueries}
          type="employee"
        />
        <PauseEmployeeModal
          id={employeesActionIds[0]}
          isOpen={isPauseEmployeeOpen}
          onClose={onClosePauseEmployee}
          onPause={handleInvalidateQueries}
        />
        <ReactivateEmployeeModal
          id={employeesActionIds[0]}
          isOpen={isReactivateEmployeeOpen}
          onClose={onCloseReactivateEmployee}
          onReactivate={handleInvalidateQueries}
        />
        <ManageUnpaidLeaveModal
          id={employeesActionIds[0]}
          isOpen={isManageUnpaidLeaveOpen}
          onClose={onCloseManageUnpaidLeave}
          onUnpaidLeave={handleInvalidateQueries}
        />
        <TerminateEmployeeModal
          id={employeesActionIds[0]}
          isOpen={isTerminateEmployeeOpen}
          onClose={onCloseTerminateEmployee}
          onTerminate={handleInvalidateQueries}
        />
        <EditEmployeeTerminationDateModal
          id={employeesActionIds[0]}
          isOpen={isEditEmployeeTerminationDateOpen}
          onClose={onCloseEditEmployeeTerminationDate}
          onEditTerminate={handleInvalidateQueries}
        />
        <RewardPointsModal
          id={employeesActionIds[0]}
          isOpen={isRewardPointsOpen}
          onClose={onCloseRewardPoints}
          onRewardPoints={handleInvalidateQueries}
        />
        <DeleteIndividualModal
          selectedIndividuals={employeesActionIds}
          isOpen={isDeleteEmployeeOpen}
          onClose={onCloseDeleteEmployee}
          onDelete={handleDeleteEmployees}
          type="employee"
        />
      </Flex>
      {isNeedPayCalendarOpen && (
        <CustomAlert
          variant="simple"
          status="warning"
          title={
            <Flex gap="8px" align="center">
              <Text>You need to have at least one pay calendar in order to proceed.</Text>
              <Link color="brand.purple" href={getPayCalendarsPath()} fontWeight="600">
                Go to pay calendars
              </Link>
            </Flex>
          }
          boxShadow="none"
          onClose={onCloseNeedPayCalendar}
        />
      )}
      <EmployeesStatus company={company} />
      <Flex justify="space-between" align="center" mt="30px" gap="30px">
        <Text as="h5" textStyle="h5SemiBold" color="brand.darkGray">
          Employees list
        </Text>
      </Flex>
      <Flex justify="space-between" align="center" gap="10px">
        <Filters
          activeFilter={activeFilter}
          displayFormatter={formatFilterValue}
          filters={filters}
          onFilterClose={() => setActiveFilter(undefined)}
          onFilterChange={handleFilterChange}
          onFilterClick={handleFilterClick}
        />
        <Spacer />
        <InputGroup
          maxW="278px"
          aria-disabled={!searchQuery.length && !employees?.list.length}
          color="brand.darkGray"
          _disabled={{ color: 'system.disabled' }}
        >
          <Input
            isDisabled={!searchQuery.length && !employees?.list.length}
            onChange={(event) => onSearchQueryChangedDebounced(event.target.value)}
            defaultValue={searchQuery}
            type="text"
            placeholder="Search"
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>
        {!isLoadingImportingBulkImports && renderBulkUploadInProgressAlert()}
      </Flex>
      {isLoadingEmployees ? (
        <Center width="full" height="full" flexGrow={1}>
          <Spinner color="brand.purple" size="xl" mb="50px" />
        </Center>
      ) : (
        <Table
          columns={getIndividualColumns('employee', actions)}
          data={employees?.list || []}
          total={employees?.total || 0}
          pageNumber={employees?.pageNumber || pageIndex}
          pageSize={employees?.pageSize || pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          getRowId={(employee) => employee.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={bulkActions}
          bulkActionsType="employee"
        />
      )}
    </AuthenticatedPageWrapper>
  );
}

export default Employees;
